// @ts-nocheck

import React, { useEffect, useRef } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import v4 from 'uuid/v4';

import { StorageKeys } from '../../../../constants/storage';
import { useHistoryItemContext } from '../../../../contexts/HistoryItemContext';
import { useLocalStorage } from '../../../../hooks';
import useMergeProps from '../../../../hooks/useMergeProps';
import { makeEntitiesBetslipsEntityStateByBetslipRefSelector } from '../../../../selectors/betslip.selector';
import { selectHistoryFilter } from '../../../../selectors/history/historyPrimary.selector';
import { BetslipFilters } from '../../../../types/history';
import { BetslipOptionsContext } from '../../context/BetslipOptionsContext';
import BetslipOptionsButton from '../BetslipOptionsButton/BetslipOptionsButton';

import BetslipOptionsModal from './BetslipOptionsModal';
import makeBetslipOptionsModalPropsSelector from './BetslipOptionsModal.selector';

type BetslipOptionsModalContainerProps = {
  betslipId?: string;
  betslipRef?: string;
  hideButtonBackground?: boolean;
  isFixed?: boolean;
  className?: string;
  isFromHistory?: boolean;
};

const BetslipOptionsModalContainer = ({
  betslipId: betslipIdFromProps,
  betslipRef: betslipReferenceFromProps,
  hideButtonBackground,
  isFromHistory = false,
  ...ownProps
}: BetslipOptionsModalContainerProps) => {
  const dispatch = useDispatch();
  const buttonRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isFirstItem } = useHistoryItemContext();
  const [isMoreButtonHasBeenClicked, markMoreButtonClicked] = useLocalStorage(
    StorageKeys.HAS_MORE_BUTTON_BEEN_VIEWED,
    false,
  );

  const selector = useMemo(() => makeBetslipOptionsModalPropsSelector(), []);
  const betslipSelector = useMemo(() => makeEntitiesBetslipsEntityStateByBetslipRefSelector(), []);
  const betslip = useSelector((state) => betslipSelector(state, { betslipReference: betslipReferenceFromProps }));
  const selectorProps = useMemo(
    () => ({ betslipId: betslipIdFromProps || betslip?.betslipId }),
    [betslip?.betslipId, betslipIdFromProps],
  );
  const filter = useSelector(selectHistoryFilter);

  const { betslipReference, ...selectedProps } = useSelector((state) => selector(state, selectorProps));

  const uuid = useMemo(() => v4(), []);

  const betslipOptionsContextValue = useMemo(
    () => ({
      betslipRef: betslipReferenceFromProps ?? betslipReference,
      betslipId: betslipIdFromProps || betslip?.betslipId,
      uuid,
      onCloseModal: () => setIsModalOpen(false),
      isFromHistory,
    }),
    [betslip?.betslipId, betslipIdFromProps, betslipReferenceFromProps, betslipReference, uuid, isFromHistory],
  );

  useEffect(() => {
    if (!betslipIdFromProps && !betslip) {
      // En faisant ca, on fait un getOneBetslip a la confirmation de pari qui est un appel inutile car on dispose
      // deja des outcomes dans notre panier.
      // A voir comment récupérer ca
      // dispatch(send({ getOneBetslip: { betslipReference: betslipReferenceFromProps } }));
    }
  }, [betslip, betslipIdFromProps, betslipReferenceFromProps, betslipReference, dispatch]);

  const onOpenModal = useCallback(() => {
    setIsModalOpen(true);

    if (!isMoreButtonHasBeenClicked) {
      markMoreButtonClicked(true);
    }
  }, [isMoreButtonHasBeenClicked, markMoreButtonClicked]);

  const onCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const otherProps = useMemo(
    () => ({
      isOpen: isModalOpen,
      onClose: onCloseModal,
      betslipRef: betslipReferenceFromProps ?? betslipReference,
      buttonRef: buttonRef.current,
    }),
    [isModalOpen, onCloseModal, betslipReferenceFromProps, betslipReference],
  );

  const enhancedProps = useMergeProps({ selectedProps, otherProps, ownProps });

  const isButtonFlashing = useMemo(
    () => isFirstItem && filter === BetslipFilters.RUNNING && !isMoreButtonHasBeenClicked,
    [isFirstItem, filter, isMoreButtonHasBeenClicked],
  );

  return (
    <BetslipOptionsContext.Provider value={betslipOptionsContextValue}>
      <BetslipOptionsModal {...enhancedProps} />
      <div ref={buttonRef}>
        <BetslipOptionsButton
          onClick={onOpenModal}
          hideButtonBackground={hideButtonBackground}
          isFlashing={isButtonFlashing}
          betslipReference={betslipReferenceFromProps || betslipReference}
          uuid={uuid}
          data-testid="betslip-options-button"
        />
      </div>
    </BetslipOptionsContext.Provider>
  );
};

export default BetslipOptionsModalContainer;
